import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a "free shipping" progress bar synced with the
 * real cart total. Re-fetches /cart.js on every `cart:update` (add, remove, and
 * quantity changes all funnel through this same event regardless of source —
 * product form, cart drawer, or cart page) rather than trusting the event's
 * `resource` payload, which is empty for some dispatch sites (e.g. the product
 * form's add-to-cart). Also re-syncs on `pageshow` when restored from the
 * browser's back/forward cache, so a cart change made on /cart is reflected
 * here after navigating back — mirrors the same pattern used by cart-icon.js.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} message
 * @property {HTMLElement} fill
 *
 * @extends {Component<Refs>}
 */
class FreeShippingBarComponent extends Component {
  requiredRefs = ['message', 'fill'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /** @returns {number} */
  get thresholdCents() {
    return Number(this.dataset.thresholdCents) || 0;
  }

  /**
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) this.refresh();
  };

  onCartUpdate = () => {
    this.refresh();
  };

  async refresh() {
    try {
      const response = await fetch('/cart.js');
      /** @type {{total_price: number, currency: string}} */
      const cart = await response.json();
      this.render(cart.total_price, cart.currency);
    } catch (error) {
      console.error('Failed to refresh the free shipping bar:', error);
    }
  }

  /**
   * @param {number} totalCents
   * @param {string} currency
   */
  render(totalCents, currency) {
    const threshold = this.thresholdCents;
    const isComplete = threshold <= 0 || totalCents >= threshold;
    const percent = threshold > 0 ? Math.min(100, (totalCents / threshold) * 100) : 100;
    const wasComplete = this.classList.contains('is-complete');

    this.refs.fill.style.width = `${percent}%`;
    this.classList.toggle('is-complete', isComplete);

    this.refs.message.textContent = isComplete
      ? '🎉 Félicitations ! Vous bénéficiez de la livraison offerte.'
      : `🚚 Plus que ${this.formatMoney(threshold - totalCents, currency)} pour bénéficier de la livraison offerte.`;

    if (isComplete && !wasComplete) {
      this.classList.remove('is-complete');
      // Force a reflow so the pop animation replays even if it already ran once.
      void this.offsetWidth;
      this.classList.add('is-complete');
    }
  }

  /**
   * @param {number} cents
   * @param {string} currency
   * @returns {string}
   */
  formatMoney(cents, currency) {
    const locale = document.documentElement.lang || 'fr';

    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }
}

if (!customElements.get('free-shipping-bar-component')) {
  customElements.define('free-shipping-bar-component', FreeShippingBarComponent);
}
